import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Inbox, Truck, Clock, MapPin } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLS = [
  { key: 'pre_transit',      Icon: Clock,  color: 'text-zinc-400' },
  { key: 'in_transit',       Icon: Truck,  color: 'text-blue-400' },
  { key: 'out_for_delivery', Icon: MapPin, color: 'text-yellow-400' },
  { key: 'delivered',        Icon: Inbox,  color: 'text-tiffany-500' },
];

/**
 * Compact admin-dashboard widget — count of active inbound packages by status.
 * Links to /admin/inbound for the full tracker.
 */
export default function InboundWidget() {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/inbound`, { headers: getAuthHeader() });
        if (!alive) return;
        setStats(r.data?.stats || null);
        setRecent((r.data?.packages || []).slice(0, 3));
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    };
    load();
    const id = setInterval(load, 120_000); // every 2 min
    return () => { alive = false; clearInterval(id); };
  }, [getAuthHeader]);

  return (
    <Link
      to="/admin/inbound"
      className="block border border-zinc-800 hover:border-tiffany-500/40 transition-colors p-5 bg-black"
      data-testid="inbound-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-mono text-sm text-zinc-400 flex items-center gap-2">
          <Inbox className="w-4 h-4" />
          INBOUND TRACKER
        </h3>
        <span className="font-mono text-[10px] text-zinc-600 tracking-wider">VIEW →</span>
      </div>

      {loading ? (
        <p className="font-mono text-xs text-zinc-600">LOADING_</p>
      ) : !stats || stats.total_active === 0 ? (
        <p className="font-mono text-xs text-zinc-500">NO ACTIVE PACKAGES</p>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {PILLS.map(({ key, Icon, color }) => {
              const n = stats.by_status?.[key] || 0;
              return (
                <div key={key} className="text-center">
                  <Icon className={`w-3.5 h-3.5 mx-auto mb-1 ${color}`} />
                  <p className={`font-mono text-base font-bold ${color}`} data-testid={`widget-inbound-${key}`}>{n}</p>
                  <p className="font-mono text-[9px] text-zinc-600 tracking-wider">{key.replace('_', ' ').toUpperCase()}</p>
                </div>
              );
            })}
          </div>
          <div className="border-t border-zinc-800 pt-3 flex items-center justify-between">
            <p className="font-mono text-[10px] text-zinc-500 tracking-widest">{stats.total_active} ACTIVE</p>
            {typeof stats.total_value === 'number' && stats.total_value > 0 && (
              <p className="font-mono text-xs text-tiffany-500">${stats.total_value.toLocaleString()}</p>
            )}
          </div>
        </>
      )}
    </Link>
  );
}
